import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  BirdPoseId,
  PlumageId,
  PublicProfile,
  SpeciesId,
} from '@dove/shared';
import { getMe, updateProfile, type MeResponse } from '@/lib/api';
import { birdPortrait, forgetBirdPortrait } from '@/lib/bird-portrait';
import { scheduleIdle } from '@/lib/idle';
import { keys, queryClient } from '@/lib/query-client';

/**
 * Who is signed in, for the whole app.
 *
 * ONE CALL, THREE ANSWERS, WHICH IS WHY THIS IS A PROVIDER AND NOT A HOOK PER
 * SCREEN. `GET /me` answers signed out, signed in but unfinished, and ready in a
 * single shape (the endpoint's own comment says why). A component that decided
 * between the sign-in screen and the app by catching a 401 would flicker between
 * them on every mount, and every screen calling `getMe` for the viewer's handle
 * would be a request per screen for a fact that changes about twice a session.
 *
 * `retry: false`, DELIBERATELY. The default retry is right for a feed and wrong
 * here: this request is the gate, so a failure has to reach the screen quickly and
 * say so rather than being retried behind a blank page. It is also the only request
 * that is EXPECTED to come back empty — signed out is a 200 with `me: null`, not an
 * error — so there is nothing to retry in the ordinary case.
 */
export interface Session {
  /** The signed-in account, or nothing. `null` while onboarding is unfinished:
   *  there is no handle yet, so there is no profile to render. */
  viewer: PublicProfile | null;
  /** Signed in, but the account does not exist yet. */
  needsOnboarding: boolean;
  /** Starting points for the onboarding fields. Suggestions, never claims. */
  suggestedHandle?: string;
  suggestedName?: string;
  /**
   * The city read off the connection this session arrived on (docs/5 § D260809-1521).
   *
   * A SUGGESTION IN THE STRICTEST SENSE OF THE WORD IN THIS INTERFACE: an IP
   * address is the network's opinion, and a VPN makes it confidently wrong. It is
   * safe only because it lands in a field somebody is looking at, beside the control
   * that replaces it with a reading from their own device.
   */
  suggestedCity?: string;
  /**
   * Where this app lives on the web, for the one thing that needs it: a link
   * somebody pastes somewhere else (docs/5 § D260805-1222, `lib/share`).
   *
   * ON THE SESSION BECAUSE IT ARRIVES WITH IT. It is a constant of the deployment
   * rather than a fact about the viewer, and it rides on `GET /me` because that call
   * already happens before the first screen — the alternative was a second boot
   * request for one string. `null` until the session resolves, and on an older API.
   */
  webOrigin: string | null;
  loading: boolean;
  /** The API could not be reached, or answered with something unusable. */
  error: unknown;
  /** Re-read the session. Awaited, so a caller can navigate after it settles. */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const query = useQuery({
    queryKey: keys.me,
    queryFn: getMe,
    retry: false,
    staleTime: 60_000,
  });

  const refresh = useCallback(async () => {
    const before = queryClient.getQueryData<MeResponse>(keys.me);
    /* Boot preloading may have cached the deliberately empty unsigned feed. A
       password sign-in or completed onboarding changes the viewer without a page
       reload, so discard that answer before `/me` opens the app gate. A normal
       profile refresh keeps the warm feed intact. */
    if (before && !before.me) {
      queryClient.removeQueries({ queryKey: keys.feed, exact: true });
    }
    await queryClient.invalidateQueries({ queryKey: keys.me });
  }, []);

  /*
   * CAPTURE YOUR OWN BIRD ONCE, HERE, AND NOWHERE ELSE (docs/5 § D260806-1118).
   *
   * A portrait is only shared if somebody's device offers it, and only the account
   * WEARING an appearance may offer it — so the surfaces that pass `mine` were the
   * composer's letterhead and the invite sheet, and somebody who opens neither
   * never seeds their own bird. Everybody who reads their letters then renders it
   * locally instead, forever, which is exactly the cost the shared copy exists to
   * remove.
   *
   * This is the one place every signed-in device passes through, so it is where the
   * offer belongs. It is not an extra render: the store asks the network before it
   * asks the GPU, so an appearance somebody else has already captured costs one
   * cheap GET — and if nobody has, this device is the one that should pay, because
   * it is about to draw this bird on every letter its owner has written.
   */
  const bird = query.data?.me?.bird;
  useEffect(() => {
    if (!bird) return;

    /* Capturing the owner's shared portrait may lazy-load Three.js, a GLB and a
       WebGL renderer. It benefits future readers but changes nothing in the
       current first viewport, so doing it while the home feed is fetching turns
       background stewardship into foreground contention. Run it at idle, with a
       bounded fallback for WebViews that do not expose requestIdleCallback. */
    return scheduleIdle(() => void birdPortrait(bird, { mine: true }));
  }, [bird]);

  const value = useMemo<Session>(
    () => ({
      viewer: query.data?.me ?? null,
      needsOnboarding: Boolean(query.data?.needsOnboarding),
      ...(query.data?.suggestedHandle
        ? { suggestedHandle: query.data.suggestedHandle }
        : {}),
      ...(query.data?.suggestedName
        ? { suggestedName: query.data.suggestedName }
        : {}),
      ...(query.data?.suggestedCity
        ? { suggestedCity: query.data.suggestedCity }
        : {}),
      webOrigin: query.data?.webOrigin ?? null,
      loading: query.isPending,
      error: query.error,
      refresh,
    }),
    [query.data, query.isPending, query.error, refresh],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside SessionProvider');
  return session;
}

/**
 * The viewer, where there certainly is one.
 *
 * EVERY SCREEN IS BEHIND THE GATE (see `AppShell`), so by the time one renders the
 * session has resolved and `viewer` is set. Screens that need your own handle, face
 * or city read it through here rather than through `useSession().viewer!` — one
 * assertion in one place, with a message, instead of a non-null operator per file.
 *
 * THERE IS ONE ROUTE OUTSIDE THE GATE NOW (`/join/:code`, docs/5 § D260805-0328) and the
 * invariant survives it: that screen has no viewer by definition, so it reads
 * `useSession()` and handles the empty case itself rather than calling this. A
 * screen reachable without an account must not use this hook.
 */
export function useViewer(): PublicProfile {
  const { viewer } = useSession();
  if (!viewer) throw new Error('No viewer: a screen rendered outside the gate.');
  return viewer;
}

/**
 * Choose a plumage, and keep it (docs/5 § D260805-0318).
 *
 * HERE, BESIDE THE READER OF THE SAME KEY, on `query-client`'s own argument: a key
 * is a contract between what reads it and what writes it, and the two halves drift
 * the moment they live in different files. `keys.me` is the viewer, the viewer is
 * where every surface gets its own bird, so this is the only writer it has.
 *
 * OPTIMISTIC, BECAUSE THE PREVIEW ALREADY SHOWED THE ANSWER. The bird on the sill
 * has been wearing the new colours since the moment they were tapped — a *Keep it*
 * that reverted the bird for the length of a round trip would read as the tap being
 * undone. So the cache is patched first and rolled back on failure, which is also
 * what makes the choice survive leaving the tab: the committed value lives in the
 * query cache rather than in the screen's state, and the screen unmounts.
 *
 * ONE INVALIDATION, NOT A SWEEP. Every author payload carries its own plumage, so
 * a colour change technically staled every cached feed row written by this account.
 * Refetching the feed, the loft, the rooms and every correspondence to recolour one
 * bird is a request storm in exchange for a cosmetic, and those caches carry the new
 * colour on their next ordinary fetch anyway (30s `staleTime`). The profile card is
 * invalidated because it is the one surface a person checks straight afterwards.
 */
export function useSetPlumage() {
  const { viewer } = useSession();
  const handle = viewer?.handle;

  return useMutation({
    mutationFn: (plumage: PlumageId) => updateProfile({ plumage }),
    onMutate: (plumage) => {
      const before = queryClient.getQueryData<MeResponse>(keys.me);
      if (before?.me) {
        queryClient.setQueryData<MeResponse>(keys.me, {
          ...before,
          me: { ...before.me, plumage },
        });
      }
      return { before };
    },
    onError: (_err, _plumage, context) => {
      /* Put the old bird back. Without this the sill keeps a colour the server
         refused, and the next `GET /me` silently contradicts it. */
      if (context?.before) queryClient.setQueryData(keys.me, context.before);
    },
    onSuccess: (result) => {
      /* The server's row, not the optimistic guess — they agree here, and taking
         the response is what keeps that true if this route ever normalises a
         value on the way in. */
      queryClient.setQueryData<MeResponse>(keys.me, (prev) =>
        prev ? { ...prev, me: result.me } : prev,
      );
      adoptOwnBird(result.me.bird);
      if (handle) {
        void queryClient.invalidateQueries({ queryKey: keys.profile(handle) });
      }
    },
  });
}

/**
 * Choose a bird, and keep it (docs/5 § D260805-1237).
 *
 * THE SAME SHAPE AS `useSetPlumage` ABOVE, deliberately, because it is the same
 * kind of promise: the sill has been showing the new bird since it was tapped, so
 * reverting for the length of a round trip would read as the tap being undone.
 *
 * WHAT IS NOT THE SAME IS THE COST OF THE PREVIEW. A plumage change is sixty-four
 * pixels on a texture the stage already has; a species change is a different GLB,
 * so `BirdStage` tears the scene down and rebuilds it. That is why the closet
 * commits this on tap rather than accumulating it as a preview to be paid for: a
 * bird you have to press *Keep it* to stop previewing would have reloaded the
 * renderer twice for one decision. Nothing is purchasable here anyway (docs/1).
 *
 * IT IS A BYLINE FIELD NOW, WHICH REVERSES A LINE THAT WAS HERE
 * (docs/5 § D260806-1118). This used to say a species could not stale any cached
 * author payload, because a species was only ever drawn on its owner's own sill.
 * The bird beside a name is a render of the actual bird now, so `PublicAuthor.bird`
 * encodes the species and every cached row written by this account carries the old
 * address. The handling is `useSetPlumage`'s and the argument is the one that
 * function gives in full: ONE INVALIDATION, NOT A SWEEP — those caches pick up the
 * new code on their next ordinary fetch, and refetching every feed, room and
 * correspondence to repaint one bird is a request storm in exchange for a cosmetic.
 */
export function useSetSpecies() {
  const { viewer } = useSession();
  const handle = viewer?.handle;

  return useMutation({
    mutationFn: (species: SpeciesId) => updateProfile({ species }),
    onMutate: (species) => {
      const before = queryClient.getQueryData<MeResponse>(keys.me);
      if (before?.me) {
        queryClient.setQueryData<MeResponse>(keys.me, {
          ...before,
          me: { ...before.me, species },
        });
      }
      return { before };
    },
    onError: (_err, _species, context) => {
      /* Put the old bird back, or the sill keeps a species the server refused and
         the next `GET /me` silently contradicts it. */
      if (context?.before) queryClient.setQueryData(keys.me, context.before);
    },
    onSuccess: (result) => {
      queryClient.setQueryData<MeResponse>(keys.me, (prev) =>
        prev ? { ...prev, me: result.me } : prev,
      );
      adoptOwnBird(result.me.bird);
      if (handle) {
        void queryClient.invalidateQueries({ queryKey: keys.profile(handle) });
      }
    },
  });
}

/**
 * Choose which moment of your bird the portrait holds (docs/5 § D260806-1118).
 *
 * THE SAME SHAPE AS THE TWO ABOVE, and it commits on tap for the reason a species
 * does — with one difference worth stating, because it is the opposite of what the
 * others are protecting. A plumage preview is held uncommitted so the big bird
 * above the drawer can wear it, and a species is committed on tap because
 * previewing one would rebuild the renderer. A pose is free to preview and there is
 * nowhere for the preview to go: the sill animates and has no posture to show, so
 * the only surface a pose changes is the portrait, and the closet's grid is already
 * drawing all eight. There would be nothing for a *Keep it* to reveal.
 */
export function useSetPose() {
  const { viewer } = useSession();
  const handle = viewer?.handle;

  return useMutation({
    mutationFn: (pose: BirdPoseId) => updateProfile({ pose }),
    onMutate: (pose) => {
      const before = queryClient.getQueryData<MeResponse>(keys.me);
      if (before?.me) {
        queryClient.setQueryData<MeResponse>(keys.me, {
          ...before,
          me: { ...before.me, pose },
        });
      }
      return { before };
    },
    onError: (_err, _pose, context) => {
      if (context?.before) queryClient.setQueryData(keys.me, context.before);
    },
    onSuccess: (result) => {
      queryClient.setQueryData<MeResponse>(keys.me, (prev) =>
        prev ? { ...prev, me: result.me } : prev,
      );
      adoptOwnBird(result.me.bird);
      if (handle) {
        void queryClient.invalidateQueries({ queryKey: keys.profile(handle) });
      }
    },
  });
}

/**
 * The appearance just committed is now OURS TO SHARE, so forget what this device
 * knows about it (docs/5 § D260806-1118).
 *
 * WHY IT IS NEEDED AT ALL. Every tile in the closet is resolved as a `preview` —
 * rendered locally and deliberately never offered to the server, because a posture
 * somebody is holding a finger on is not an appearance anybody wears. The instant it
 * is kept, that stops being true: it is the account's committed bird, it is about to
 * be drawn beside their name on every letter they have written, and it is exactly
 * the picture worth capturing once for everybody who will see it. Dropping the entry
 * makes the next render take the ordinary path — which asks the server, finds
 * nothing, renders, and seeds.
 */
function adoptOwnBird(code: string | undefined): void {
  if (code) forgetBirdPortrait(code);
}
